/**
 * Analytics for the 2610 chat, preserved across the DTA migration.
 *
 * WHY THIS FILE EXISTS
 *
 * The events below were fired inline inside the DTA component. Deleting that
 * component without first extracting them is how a migration silently drops
 * analytics: nothing fails, no test breaks, and the dashboards just flatten.
 *
 * INVENTORY OF WHAT THE DTA INTEGRATION ACTUALLY TRACKED
 *
 * Four events, all in components/2610/chatbot/index.js, and every one of them is
 * about the launcher or the gate. None of them observed a conversation:
 *
 *   chat_anchor_click   launcher clicked
 *   chat_modal_opened   gate modal shown
 *   chat_modal_closed   gate modal dismissed
 *   chat_initialized    gate passed, chat handed the visitor's email
 *
 * The gate's own events live in chat-init-modal (verification_email_sent,
 * verification_email_failed, user_registered) and email-verification-modal
 * (email_verified, email_verification_failed). Those components are unchanged, so
 * those events are unaffected.
 *
 * Two consequences worth stating plainly:
 *
 * 1. There is NOTHING to re-attach for sends, responses, errors or feedback,
 *    because DTA never reported them. The conversation events further down are new
 *    coverage, not a migration.
 * 2. The `data-gtm-category` / `data-gtm-action` / `data-gtm-label` attributes on
 *    <dta-chat> and <dta-chat-anchor> were never read by anything. The generic
 *    tracker in components/global/google-tag-manager derives its payload from
 *    element type, id, class, text and href, and infers component_context by
 *    walking up looking for a class containing "chatbot" — not from those
 *    attributes. Keeping the launcher inside .chatbot-container is therefore what
 *    preserves the generic click_tracking event; copying the attributes across
 *    would achieve nothing.
 */

// Bumped per deployment, carried on the launcher event exactly as before so the
// two are comparable across the migration.
export const FRONTEND_VERSION = '2.0.0';

function push(payload) {
  // GTM may not have initialised yet, or may be blocked. Analytics must never be
  // the reason the chat fails to work.
  if (typeof window === 'undefined' || !window.dataLayer) return;
  try {
    window.dataLayer.push({
      current_language: window.current_language || 'pt',
      component_context: 'chatbot',
      ...payload,
    });
  } catch (ignored) {
    /* analytics is best-effort */
  }
}

function domainOf(email) {
  if (!email || typeof email !== 'string') return 'unknown';
  const parts = email.split('@');
  return parts.length > 1 ? parts[1] : 'unknown';
}

// --- preserved events -------------------------------------------------------
//
// Names and payload keys are unchanged from the DTA implementation, so existing
// GTM triggers, GA4 custom definitions and saved reports keep working. Only
// `element_type` and `event_label` change, and they have to: they named a DTA
// element that no longer exists. `chat_type` is added to every one of them so a
// report can separate legacy DTA traffic (2510) from the in-house widget (2610)
// during the transition.

export function trackLauncherClick({ chatInitialized }) {
  push({
    event: 'chat_anchor_click',
    event_category: 'Chat Interaction',
    event_action: 'Chat Anchor Click',
    event_label: 'Protheus Chat Button',
    chat_initialized: Boolean(chatInitialized),
    element_type: 'chat-launcher',
    frontend_version: FRONTEND_VERSION,
    chat_type: 'protheus_release_widget',
  });
}

export function trackGateOpened() {
  push({
    event: 'chat_modal_opened',
    event_category: 'Chat Interaction',
    event_action: 'Modal Opened',
    event_label: 'Chat Initialization Modal',
    modal_type: 'chat_init_modal',
    chat_type: 'protheus_release_widget',
  });
}

export function trackGateClosed() {
  push({
    event: 'chat_modal_closed',
    event_category: 'Chat Interaction',
    event_action: 'Modal Closed',
    event_label: 'Chat Initialization Modal',
    modal_type: 'chat_init_modal',
    chat_type: 'protheus_release_widget',
  });
}

export function trackChatInitialized(email) {
  push({
    event: 'chat_initialized',
    event_category: 'Chat Interaction',
    event_action: 'Chat Initialized',
    event_label: 'User Email Provided',
    user_email_domain: domainOf(email),
    chat_type: 'protheus_release_widget',
  });
}

// --- new conversation coverage ----------------------------------------------
//
// DTA reported none of this. It is worth having precisely because the new backend
// has properties the old one did not: a turn can take over two minutes, every turn
// is billed, and failures arrive with a stable code. Turn duration and error code
// are the two numbers that decide whether this pilot is viable, and neither is
// observable without these events.
//
// No message text is ever sent. Question content is user data and has no business
// in an analytics payload; length is recorded instead, which answers "are people
// asking long questions" without retaining what they asked.

export function trackMessageSent({ messageLength, turnIndex }) {
  push({
    event: 'chat_message_sent',
    event_category: 'Chat Interaction',
    event_action: 'Message Sent',
    event_label: 'Visitor Question',
    message_length: messageLength,
    turn_index: turnIndex,
    chat_type: 'protheus_release_widget',
  });
}

export function trackResponseReceived({ durationMs, answerLength, turnIndex }) {
  push({
    event: 'chat_response_received',
    event_category: 'Chat Interaction',
    event_action: 'Response Received',
    event_label: 'Agent Answer',
    // The number that decides whether the "one answer at the end" tradeoff is
    // acceptable to real users.
    duration_ms: durationMs,
    duration_bucket: durationBucket(durationMs),
    answer_length: answerLength,
    turn_index: turnIndex,
    chat_type: 'protheus_release_widget',
  });
}

export function trackResponseFailed({ code, requestId, durationMs, turnIndex }) {
  push({
    event: 'chat_response_failed',
    event_category: 'Chat Interaction',
    event_action: 'Response Failed',
    // The stable code, never the display message: the codes are a contract and
    // the strings are not, so grouping on the label would fragment the report the
    // first time a message is reworded.
    event_label: code,
    error_code: code,
    // On screen for the visitor and in the report for support: it is the only
    // pointer into the agent's server log for this failure.
    request_id: requestId || '',
    duration_ms: durationMs,
    turn_index: turnIndex,
    chat_type: 'protheus_release_widget',
  });
}

export function trackTurnCancelled({ elapsedMs, turnIndex }) {
  push({
    event: 'chat_turn_cancelled',
    event_category: 'Chat Interaction',
    event_action: 'Turn Cancelled',
    event_label: 'Visitor Stopped',
    // How long people are willing to wait before giving up, which is the other
    // half of the duration question.
    elapsed_ms: elapsedMs,
    turn_index: turnIndex,
    chat_type: 'protheus_release_widget',
  });
}

export function trackPanelToggled({ open }) {
  push({
    event: 'chat_panel_toggled',
    event_category: 'Chat Interaction',
    event_action: open ? 'Panel Opened' : 'Panel Closed',
    event_label: open ? 'Open' : 'Closed',
    chat_type: 'protheus_release_widget',
  });
}

export function trackFeedback({ vote, messageId }) {
  push({
    event: 'chat_feedback',
    event_category: 'Chat Interaction',
    event_action: 'Feedback Given',
    event_label: vote,
    feedback_vote: vote,
    message_id: messageId || '',
    chat_type: 'protheus_release_widget',
  });
}

/**
 * Coarse duration buckets, because the raw millisecond value is unusable as a GA4
 * dimension: it is effectively unique per turn. Boundaries follow the measured
 * behaviour of this agent rather than round numbers — ~7s at the fast end, ~139s
 * observed, 285s hard ceiling.
 */
function durationBucket(durationMs) {
  if (typeof durationMs !== 'number' || Number.isNaN(durationMs)) return 'unknown';
  if (durationMs < 10000) return '0-10s';
  if (durationMs < 30000) return '10-30s';
  if (durationMs < 60000) return '30-60s';
  if (durationMs < 120000) return '60-120s';
  if (durationMs < 285000) return '120-285s';
  return '285s+';
}

export { durationBucket };
